"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import OptimizedPicture from "./_lib/OptimizedPicture";
import { HoverGlow, trackHoverGlow } from "./_lib/HoverGlow";
import { usePrefersReducedMotion } from "./_lib/usePrefersReducedMotion";

gsap.registerPlugin(ScrollTrigger);

interface TeamMember {
  name: string;
  role: string;
  image: string;
  video?: string;
}

const TEAM: TeamMember[] = [
  {
    name: "Afiola Etomi",
    role: "Pedagogically enhancing lessons",
    image: "/assets/team/afiola-etomi-poster.jpg",
    video: "/Afi.mp4",
  },
  {
    name: "Carme Gener Villalonga",
    role: "Best-practice motivational design",
    image: "/assets/team/carme-gener-villalonga-poster.jpg",
    video: "/Carme.mp4",
  },
  {
    name: "Kshitij Kumar",
    role: "Data and the AI feedback loop",
    image: "/assets/team/kshitij-kumar-poster.png",
    video: "/Kshitij.mp4",
  },
  {
    name: "Ruslan Padnevych",
    role: "Secure and stable system",
    image: "/assets/team/ruslan-padnevych-poster.jpg",
    video: "/Ruslan.mp4",
  },
];

function PlayIcon() {
  return (
    <svg
      width="19"
      height="23"
      viewBox="0 0 19.0882 22.8767"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.81669 0.505853C2.74001 -0.837882 0 0.652752 0 3.12626V19.7504C0 22.2239 2.74 23.7146 4.81668 22.3709L17.6626 14.0588C19.5634 12.8289 19.5634 10.0479 17.6627 8.81795L4.81669 0.505853Z"
        fill="white"
      />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect width="14" height="14" rx="2" fill="white" />
    </svg>
  );
}

function VideoSpinner() {
  return (
    <div
      role="status"
      aria-label="Loading video"
      className="h-10 w-10 animate-spin rounded-full border-2 border-white/25 border-t-white"
    />
  );
}

function TeamCard({
  member,
  cardIndex,
  activeCardIndex,
  onActiveChange,
  cardRef,
}: {
  member: TeamMember;
  cardIndex: number;
  activeCardIndex: number | null;
  onActiveChange: (index: number | null) => void;
  cardRef: (el: HTMLDivElement | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasVideo = Boolean(member.video);

  const stopVideo = useCallback(
    (clearActive = true) => {
      const video = videoRef.current;
      if (!video) return;

      video.pause();
      video.currentTime = 0;
      setIsLoading(false);
      setIsPlaying(false);
      if (clearActive) onActiveChange(null);
    },
    [onActiveChange]
  );

  useEffect(() => {
    if (activeCardIndex === null || activeCardIndex === cardIndex) return;
    if (!isPlaying && !isLoading) return;
    stopVideo(false);
  }, [activeCardIndex, cardIndex, isPlaying, isLoading, stopVideo]);

  const handleMediaClick = () => {
    if (!member.video || !videoRef.current || isLoading) return;

    const video = videoRef.current;
    if (!video.paused) {
      stopVideo();
      return;
    }

    onActiveChange(cardIndex);
    setIsLoading(true);
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        setIsLoading(false);
        onActiveChange(null);
      });
    }
  };

  const handleVideoPlaying = () => {
    setIsLoading(false);
    setIsPlaying(true);
  };

  const handleStop = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    stopVideo();
  };

  return (
    <div
      ref={cardRef}
      className="flex-1 min-w-0"
    >
      <div className="flex flex-col gap-6">
        <div
          className="group relative aspect-[242/441] w-full"
          onMouseMove={trackHoverGlow}
        >
          <div
            className={`relative h-full w-full rounded-2xl overflow-hidden transition-transform duration-300 ease-out group-hover:scale-[1.03] ${hasVideo ? "cursor-pointer" : ""}`}
            onClick={handleMediaClick}
            onKeyDown={(event) => {
              if (!hasVideo) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleMediaClick();
              }
            }}
            role={hasVideo ? "button" : undefined}
            tabIndex={hasVideo ? 0 : undefined}
            aria-label={
              hasVideo
                ? isLoading
                  ? `Loading ${member.name} video statement`
                  : isPlaying
                    ? `Stop ${member.name} video statement`
                    : `Play ${member.name} video statement`
                : undefined
            }
            aria-busy={isLoading || undefined}
          >
            {member.video ? (
              <video
                ref={videoRef}
                src={member.video}
                poster={member.image}
                preload="none"
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
                onPlaying={handleVideoPlaying}
                onEnded={() => stopVideo()}
                onError={() => {
                  setIsLoading(false);
                  setIsPlaying(false);
                  onActiveChange(null);
                }}
              />
            ) : null}
            {isPlaying ? (
              <div className="absolute inset-0 z-10 hidden md:flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={handleStop}
                  aria-label={`Stop ${member.name} video statement`}
                  className="flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/80 focus-visible:outline-offset-4"
                >
                  <StopIcon />
                </button>
              </div>
            ) : null}
            {!isPlaying ? (
              <>
                <OptimizedPicture
                  src={member.image}
                  alt={member.name}
                  loading="lazy"
                  pictureClassName="absolute inset-0"
                  imgClassName="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20" />
                {!isLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayIcon />
                  </div>
                ) : null}
              </>
            ) : null}
            {isLoading ? (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40">
                <VideoSpinner />
              </div>
            ) : null}
          </div>
          <HoverGlow scaleOnHover />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-[18px] md:text-[18px] font-semibold leading-[28px] md:leading-[32px] text-white">
            {member.name}
          </p>
          <p className="text-[14px] md:text-[16px] font-normal leading-[20px] md:leading-[24px] text-white/80">
            {member.role}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function TeamSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      const cards = cardsRef.current.filter(Boolean) as HTMLDivElement[];

      if (prefersReducedMotion) {
        if (headingRef.current) gsap.set(headingRef.current, { opacity: 1, y: 0 });
        if (cards.length) gsap.set(cards, { opacity: 1, scale: 1 });
        return;
      }

      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: headingRef.current,
              start: "top 85%",
              toggleActions: "play none none none",
            },
          }
        );
      }

      const center = (cards.length - 1) / 2;
      cards.forEach((card, i) => {
        const distFromCenter = Math.abs(i - center);
        gsap.fromTo(
          card,
          { opacity: 0, scale: 0.92 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.7,
            delay: distFromCenter * 0.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 90%",
              toggleActions: "play none none none",
            },
          }
        );
      });
    }, sectionRef.current);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      ref={sectionRef}
      id="team"
      className="relative w-full py-16 md:py-[120px] overflow-hidden"
    >
      <div className="max-w-[1040px] mx-auto px-5">
        <h2
          ref={headingRef}
          className="text-[32px] md:text-[40px] font-semibold leading-[40px] md:leading-[48px] mb-12 md:mb-16 bg-clip-text text-transparent"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #ffffff 0%, #b7a8ea 100%)",
          }}
        >
          Listen to how our team thinks about Language+
        </h2>

        <div className="grid grid-cols-1 min-[500px]:grid-cols-2 lg:grid-cols-4 gap-6">
          {TEAM.map((member, i) => (
            <TeamCard
              key={`${member.name}-${i}`}
              member={member}
              cardIndex={i}
              activeCardIndex={activeCardIndex}
              onActiveChange={setActiveCardIndex}
              cardRef={(el) => {
                cardsRef.current[i] = el;
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
